import { Icons, SearchParams, YadoHotel, YadoPlan, YadoRoom } from '../types';
import styles from '../styles/HotelPlansSearch.module.scss';
import PackageList from './PackageList';
import PackageItem from './PackageItem';
import { setupPhotoGallery } from '../../AccommodationUI/accommodations/photo_gallery';
import { getImage } from '../../utils/pathHelper';
import roomImageIcon from '../images/image.svg';
import clockIcon from '../images/clock.svg';
import i18n from "../../i18n";
import { subDays } from 'date-fns';
import { formatDateWithDayOfWeek } from '../../utils/dateHelper';

type RoomItemProps = {
  hotel: YadoHotel;
  room: YadoRoom;
  icons: Icons;
  query: SearchParams;
  nearestResortCode: string;
};

const RoomItem: React.FC<RoomItemProps> = ({ hotel, room, icons, query, nearestResortCode }) => {
  setupPhotoGallery();

  const gon = (window as any).gon;
  const locale = gon.locale;
  const mainAmenities = ["smoking", "bed_type", "free_wifi", "size", "private_open_air_bath"];
  const excludedAmenities = ["number_of_beds", "capacity", "min_capacity", "wifi", "bed_type_keys"];
  const roomInfo = room.room_information || {};
  const otherRoomInfo = room.localized_other_room_facilities || {};
  const localizedRoomInfo = room.localized_room_information || {};
  const reservationDeadline = subDays(query.checkinDate, room.yado_plan.closing_days_before_checkin);

  const localizedAttributes = {
    mainAmenities: Object.entries(roomInfo)
      .filter(([key, value]) => (mainAmenities.includes(key) && (key === 'smoking' || value)))
      .reduce((acc, [key, value]) => {
        acc[key] = localizedRoomInfo[key];
        return acc;
      }, {} as Record<string, string | string[]>),

    otherAmenities: Object.entries(roomInfo)
      .filter(([key, value]) => !mainAmenities.includes(key) && !excludedAmenities.includes(key) && value)
      .reduce((acc, [key, value]) => {
        acc[key] = localizedRoomInfo[key];
        return acc;
      }, {} as Record<string, string>)
  };

  const getAmenityIcon = (key: string) => {
    switch (key) {
      case "smoking":
        return roomInfo[key] ? icons.smokingIcon : icons.noSmokingIcon;
      case "bed_type":
        return icons.bedIcon;
      case "free_wifi":
        return icons.wifiIcon;
      case "size":
        return icons.yadoIcon;
      case "private_open_air_bath":
        return icons.onsenIcon;
      default:
        return null;
    }
  };

  const sortedPackages = room.yado_plan.packages.sort((a, b) => {
    const is_a_nearest = a.common_resorts.length === 1 && a.common_resorts[0].code === nearestResortCode ? 1 : 0
    const is_b_nearest = b.common_resorts.length === 1 && b.common_resorts[0].code === nearestResortCode ? 1 : 0

    // 単体リゾートかつ最も近ければ上
    if (is_a_nearest > is_b_nearest) return -1
    if (is_b_nearest > is_a_nearest) return 1

    const price_a = Number(a.total_price.amount.replace(',', ''))
    const price_b = Number(b.total_price.amount.replace(',', ''))

    // その中で安い方が上
    if (price_a < price_b) return -1
    if (price_b < price_a) return 1

    return 0
  });

  const MealTag: React.FC<{plan: YadoPlan}> = ({plan}) => {
    const { breakfast, dinner } = plan;

    if (breakfast && dinner) {
      return (
        <div className={`${styles.mealTag} ${styles.bothMeal}`}>
          <span>
            <img src={icons.foodOrangeIcon} alt="food" className="mr-2" />{i18n.t('accommodations.breakfast_and_dinner_included')}
          </span>
        </div>
        )
    }

    if (breakfast || dinner) {
      const text = breakfast ? i18n.t('accommodations.breakfast_included') : i18n.t('accommodations.dinner_included')
      return (
        <div className={`${styles.mealTag} ${styles.eitherMeal}`}>
          <span>
            <img src={icons.foodIcon} alt="food" className="mr-2" />{text}
          </span>
        </div>
        )
    };

    return (
      <div className={`${styles.mealTag} ${styles.noMeal}`}>
      <span>
        <img src={icons.noFoodGrayIcon} alt="no food" className="mr-2" />{i18n.t('accommodations.overnight_stay_without_meals')}
      </span>
    </div>
    )
  }

  return (
    <div className={`${styles.roomItem} rounded-xl`}>
      <div className={`${styles.roomTitle} p-3 mb-2`}>
        <h3 className="m-0">{room.localized_name[locale]}</h3>
      </div>
      <div className="row no-gutters px-3">
        <MealTag plan={room.yado_plan} />
      </div>
      <div className={`row no-gutters px-3 my-2 ${styles.closingDate}`}>
        <img src={getImage(clockIcon)} alt="more images" className="mr-1" />
        <div>
          {i18n.t('stay_packages.hotels.show.reservations_for_this_checkin_date_can_be_made_up_until', {date: formatDateWithDayOfWeek(reservationDeadline)})}
        </div>
      </div>
      <div className="row no-gutters">
        <div className="col-lg-4 col-12 p-3">
          <a href="#" data-src={`#room-gallery-${room.id}`} className="open-gallery">
            <div className={styles.roomImageWrapper}>
              <img src={room.images[0]} alt={room.localized_name[locale]} className={styles.roomImage} />
              <div
                className="photo_more rounded-pill d-flex align-items-center">
                <img src={getImage(roomImageIcon)} alt="more images" className="mr-1" />
                {room.images.length}
              </div>
            </div>
          </a>
          <div className={styles.roomInfo}>
            <div className={styles.roomFacilities}>
              <div className={styles.facilityHeader}>{i18n.t('accommodations.main_facility')}</div>
              <ul className={`${styles.facilityList} ${styles.mainFacilityList}`}>
                {Object.entries(localizedAttributes.mainAmenities).map(([key, translatedValue]) => {
                  const icon = getAmenityIcon(key);
                  return (key === "bed_type") && Array.isArray(translatedValue) ?
                    <li key={key} className='d-flex'>
                      {icon && <img className="mr-2" src={icon} alt={key} />}
                      <div className='d-flex flex-column'>
                        {translatedValue.map((value, i) => {
                          return <span>{value} × {roomInfo['number_of_beds'][i]}</span>
                        })}
                      </div>
                    </li>
                  :
                    <li key={key}>
                      {icon && <img className="mr-2" src={icon} alt={key} />}
                      {translatedValue}
                    </li>
                  }
                )}
              </ul>
              <div className={styles.facilityHeader}>{i18n.t('accommodations.amenities')}</div>
              <ul className={`${styles.facilityList} ${styles.amenityList}`}>
                {Object.entries(localizedAttributes.otherAmenities).map(([key, translatedValue]) => (
                  <li key={key}>{translatedValue}</li>
                ))}
              </ul>
              {otherRoomInfo[locale]?.length > 0 && (
                <>
                  <div className={styles.facilityHeader}>{i18n.t('accommodations.other')}</div>
                  <div className={`${styles.facilityList} ${styles.otherAmenityList}`}>
                    <p>{otherRoomInfo[locale]}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-8 col-12 px-3">
          <div className={styles.packagesWrapper}>
            { room.yado_plan.included_lift_ticket ?
              <PackageItem
                hotel={hotel}
                room={room}
                currency={room.yado_plan.included_lift_ticket.total_price['currency']}
                localizedItemName={room.yado_plan.included_lift_ticket.localized_name}
                localizedAttributes={localizedAttributes}
                isIncludingTicketItem={true}
                icons={icons}
                packageItem={room.yado_plan.packages[0]}
                query={query}
              />
            :
              <PackageList
                hotel={hotel}
                room={room}
                packages={sortedPackages}
                localizedAttributes={localizedAttributes}
                icons={icons}
                query={query}
              />
            }
          </div>
        </div>
      </div>
      <div style={{ display: 'none' }}>
        <div id={`room-gallery-${room.id}`} style={{ backgroundColor: '#0005' }}>
          <div className="container">
            <div className="row mx-md-5 mt-2">
              {room.images.map((image_url, index) => (
                <a
                  key={index}
                  className="col-6 photo"
                  data-fancybox="accommodation-image"
                  data-type="image"
                  href={image_url}
                  style={{
                    outline: 0,
                    background: `url(${image_url}) no-repeat center center`,
                    backgroundSize: 'cover'
                  }}
                ></a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomItem;
